import { z } from "zod";
import { contributionLineSchema, destinationStRuleResultSchema, fcpInAlqIntraResultSchema, goodsOriginSchema, pisCofinsProfileResultSchema, pisCofinsRegimeSchema, purchaseOwnIcmsRateSchema, resalePisCofinsTreatmentSchema, rulesetSelectorSchema, saleGeneralRuleResultSchema, stCollectionResultSchema, statutoryOwnIcmsRateResultSchema, ufSchema, } from "./calculation.js";
import { operationStReviewSchema } from "./operation-st-review.js";
export const OPERATION_RESEARCH_HEADER = "x-tax-operation-research";
export const operationResearchVersionSchema = z.literal("1.0.0");
export const operationResearchStateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    snapshotId: z.string().uuid(),
    rootSnapshotId: z.string().uuid(),
    previousSnapshotId: z.string().uuid().nullable(),
    revision: z.number().int().positive(),
    requestDigest: z.string().regex(/^[a-f0-9]{64}$/),
    resultDigest: z.string().regex(/^[a-f0-9]{64}$/),
    rulesetVersion: z.string().min(1),
    state: z.enum(["NOT_NEEDED", "DISABLED", "PENDING", "REVIEW_REQUIRED", "RECALCULABLE"]),
    /** Existing published snapshot is immutable; new evidence never alters its amounts. */
    recalculation: z.literal("EXPLICIT_NEW_REVISION"),
    gaps: z.number().int().nonnegative(),
    requestIds: z.array(z.string().uuid()).max(200),
    blockers: z.array(z.string().regex(/^[A-Z][A-Z0-9_]{1,79}$/)).max(20),
    createdAt: z.string().datetime(),
})
    .strict();
export const operationRecalculateRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /** Explicit published version; ACTIVE is intentionally unavailable for a revision. */
    rulesetVersion: z.string().min(1).max(80),
})
    .strict();
/**
 * Resale operation (R-02): supplier (A) → BackChannel establishment (B) →
 * customer (C). The Tax Platform returns the taxes of both legs from published
 * rules; sale value is either a commercial input or derived by explicit TR_V1 opt-in.
 * Federal contributions require the versioned, explicit federalTaxes opt-in.
 */
const moneySchema = z.string().regex(/^(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
const unitValueSchema = z.string().regex(/^(0|[1-9]\d{0,15})(\.\d{1,6})?$/);
const quantitySchema = z.string().regex(/^[1-9]\d{0,8}$/);
export const operationSupplierTypeSchema = z.enum(["INDUSTRY", "DISTRIBUTOR"]);
/** Signed: a credit that exceeds every debit leaves a negative net burden. */
const signedMoneySchema = z.string().regex(/^-?(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
export const operationTrPricingInputSchema = z
    .object({
    version: z.literal("TR_V1"),
    method: z.literal("COST_PLUS_TR"),
    /** Commercial share of the sale price, not a fiscal rate or a net margin. */
    rate: z.string().regex(/^(0(\.\d{1,8})?)$/),
})
    .strict();
/** Explicit SP-hub quote only; it does not qualify recipient ST or a net target margin. */
export const operationSpHubTrPricingInputSchema = operationTrPricingInputSchema.extend({
    version: z.literal("TR_SP_HUB_V1"),
});
export const operationNetTargetPricingInputSchema = operationSpHubTrPricingInputSchema.extend({
    version: z.literal("NET_SP_HUB_V1"),
    method: z.literal("NET_TARGET_MARGIN"),
    /** Explicit line totals. Missing expenses must never silently become zero. */
    expenses: z
        .object({ operations: moneySchema, commission: moneySchema, financing: moneySchema })
        .strict(),
});
export const operationPricingInputSchema = z.discriminatedUnion("version", [
    operationTrPricingInputSchema,
    operationSpHubTrPricingInputSchema,
    operationNetTargetPricingInputSchema,
]);
export const operationAcquisitionStBasisSchema = z.enum([
    "RESOLVED_NON_ST",
    "PRIOR_ST_INCLUDED",
    "DECLARED_INVOICE_ST",
    "RESOLVED_SENDER_ST",
]);
export const operationPricingBlockerSchema = z.enum([
    "INPUT_INVALID",
    "PURCHASE_RULE_PENDING",
    "PURCHASE_IPI_PENDING",
    "PURCHASE_FCP_PENDING",
    "PURCHASE_ST_PENDING",
    "SALE_RULE_PENDING",
    "MONETARY_LIMIT",
    "FEDERAL_TAXES_PENDING",
    "SALE_FCP_PENDING",
    "NET_TARGET_UNREACHABLE",
    "PRICING_PRECISION_LIMIT",
]);
export const operationTrPricingResultSchema = operationTrPricingInputSchema
    .extend({
    costBasisKind: z.literal("PURCHASE_INVOICE"),
    costBasis: moneySchema,
    saleLineValue: moneySchema,
    purchaseValue: unitValueSchema,
    quantity: quantitySchema.optional(),
    /** Display approximation only; saleLineValue remains authoritative. */
    unitSaleValueApproximation: unitValueSchema.optional(),
    roundingScale: z.literal(2),
    roundingMode: z.literal("HALF_UP"),
})
    .strict();
export const operationSpHubTrPricingResultSchema = operationTrPricingResultSchema.extend({
    version: z.literal("TR_SP_HUB_V1"),
    /** Provenance for the invoice-cost quote, not proof of payment or documentary review. */
    acquisitionStBasis: operationAcquisitionStBasisSchema,
});
export const operationNetTargetPricingResultSchema = operationSpHubTrPricingResultSchema.extend({
    ...operationNetTargetPricingInputSchema.shape,
    marginBasis: z.literal("EARNINGS_CONTRIBUTION"),
    scope: z.literal("OPERATION_FISCAL_AND_DECLARED_EXPENSES"),
    totalExpenses: moneySchema,
    purchaseCredits: moneySchema,
    contribution: signedMoneySchema,
    achievedMargin: z.string().regex(/^-?(0|[1-9]\d{0,5})(\.\d{1,8})?$/),
    /** Cent-rounded difference from target × sale; nearest cent, not an exact guarantee. */
    targetDifference: signedMoneySchema,
});
export const operationPricingResultSchema = z.discriminatedUnion("version", [
    operationTrPricingResultSchema,
    operationSpHubTrPricingResultSchema,
    operationNetTargetPricingResultSchema,
]);
const rateSchema = z.string().regex(/^-?(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
export const operationFederalInputSchema = z
    .object({
    version: z.literal("GOVERNED_V1"),
    regime: pisCofinsRegimeSchema,
    /** A declaration for this acquisition, not accreditation of the legal entity. */
    purchaseCreditEligibility: z.enum(["UNKNOWN", "ELIGIBLE", "INELIGIBLE"]),
    treatment: resalePisCofinsTreatmentSchema.optional(),
    /** Explicit fiscal treatment when prior ST closes the internal sale. */
    stResalePisCofins: z.literal("ON_SALE_PRICE").optional(),
})
    .strict();
export const operationFederalLineSchema = z
    .object({
    status: z.enum(["CALCULATED", "PENDING"]),
    base: moneySchema.nullable(),
    pis: contributionLineSchema,
    cofins: contributionLineSchema,
    pisRuleId: z.string().uuid().nullable(),
    cofinsRuleId: z.string().uuid().nullable(),
    pisAmount: moneySchema.nullable(),
    cofinsAmount: moneySchema.nullable(),
    total: moneySchema.nullable(),
})
    .strict();
export const operationFederalResultSchema = z
    .object({
    version: z.literal("GOVERNED_V1"),
    status: z.enum(["CALCULATED", "PENDING"]),
    declaration: operationFederalInputSchema,
    profile: pisCofinsProfileResultSchema,
    pending: z.array(z.enum([
        "FISCAL_LEGS_PENDING",
        "REGIME_UNKNOWN",
        "PROFILE_PENDING",
        "CREDIT_ELIGIBILITY_UNKNOWN",
        "ST_RESALE_BASIS_UNKNOWN",
        "POST_2026_REGIME",
        "PURCHASE_OWN_ICMS_UNKNOWN",
        "RATE_RULE_AMBIGUOUS",
    ])),
    purchaseCredit: operationFederalLineSchema,
    saleDebit: operationFederalLineSchema,
    /** Separate contribution ledgers: a PIS credit never offsets COFINS. */
    pisPayable: moneySchema.nullable(),
    cofinsPayable: moneySchema.nullable(),
    payable: moneySchema.nullable(),
    creditCarryover: moneySchema.nullable(),
})
    .strict();
const operationItemBaseSchema = z
    .object({
    itemId: z.string().min(1).max(100),
    federalTaxes: operationFederalInputSchema.optional(),
    ncm: z.string().regex(/^\d{8}$/),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .optional(),
    /** NF-e `orig` 0–8. Imported origins (1, 2, 3, 8) take the 4 % interstate rate on the purchase. */
    goodsOrigin: goodsOriginSchema.optional(),
    supplierUf: ufSchema,
    centerUf: ufSchema,
    customerUf: ufSchema,
    /** Contributor buying for resale/industry. False = final consumer (DIFAL). */
    customerIcmsTaxpayer: z.boolean(),
    /** Absent preserves the original published-rule calculation. Distributor means an ordinary reseller, not an industrial-equivalent establishment. */
    supplierType: operationSupplierTypeSchema.optional(),
    /** Confirmed prior ST in the price, with no new charge. Internal purchases only; interstate withholding uses purchaseStRetained. */
    purchaseStPreviouslyPaid: z.boolean().optional(),
    /** When present, purchaseValue/saleValue are unit values, up to six decimals. Multiply before rounding the line to cents. */
    quantity: quantitySchema.optional(),
    /** Goods before IPI/ST: total when quantity is absent, unit value otherwise. */
    purchaseValue: unitValueSchema,
    /**
     * ICMS-ST already retained on the supplier invoice (CST 10/60 on entry), as the invoice shows
     * it. When stated it is the entry ST (not recomputed), and it closes an internal sale (CST 60).
     */
    purchaseStRetained: moneySchema.optional(),
})
    .strict();
export const operationItemInputSchema = z
    .union([
    operationItemBaseSchema.extend({
        /** Manual sale before ST: total without quantity, unit value with quantity. */
        saleValue: unitValueSchema,
        pricing: z.never().optional(),
    }),
    operationItemBaseSchema.extend({
        saleValue: z.never().optional(),
        pricing: operationPricingInputSchema,
    }),
])
    .superRefine((item, ctx) => {
    if (item.pricing?.version === "NET_SP_HUB_V1" && item.federalTaxes === undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["federalTaxes"],
            message: "Net-target pricing requires an explicit governed federal declaration",
        });
    }
    if (item.pricing &&
        (item.centerUf !== "SP" ||
            !item.supplierType ||
            (item.pricing.version === "TR_V1" &&
                (item.supplierUf !== "SP" || item.customerUf !== "SP")))) {
        ctx.addIssue({
            code: "custom",
            path: ["pricing"],
            message: item.pricing.version === "TR_V1"
                ? "TR_V1 requires SP to SP to SP and an explicit supplier type"
                : `${item.pricing.version} requires the SP center and an explicit supplier type`,
        });
    }
    for (const field of ["purchaseValue", "saleValue"]) {
        if (item[field] === undefined)
            continue;
        if (item.quantity === undefined && !moneySchema.safeParse(item[field]).success) {
            ctx.addIssue({
                code: "custom",
                path: [field],
                message: "Totals accept at most two decimals; supply quantity for unit values",
            });
        }
        if (item.quantity !== undefined &&
            quantitySchema.safeParse(item.quantity).success &&
            unitValueSchema.safeParse(item[field]).success) {
            const [whole = "0", fraction = ""] = item[field].split(".");
            const scaled = BigInt(whole + fraction.padEnd(6, "0")) * BigInt(item.quantity);
            if (scaled >= 10n ** 22n - 5000n) {
                ctx.addIssue({
                    code: "custom",
                    path: [field],
                    message: "Line total exceeds the monetary limit",
                });
            }
        }
    }
    if (item.purchaseStPreviouslyPaid && item.supplierUf !== item.centerUf) {
        ctx.addIssue({
            code: "custom",
            path: ["purchaseStPreviouslyPaid"],
            message: "Interstate purchases require the destination withholding amount, not an assumption of prior ST",
        });
    }
    if (item.purchaseStPreviouslyPaid && item.purchaseStRetained !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["purchaseStRetained"],
            message: "Choose prior ST included in the price or a separate invoice ST amount",
        });
    }
});
export const operationSimulationRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    calculationDate: z.string().date(),
    rulesetSelector: rulesetSelectorSchema,
    items: z.array(operationItemInputSchema).min(1).max(50),
})
    .strict()
    .superRefine((request, context) => {
    const ids = new Set();
    for (const [index, item] of request.items.entries()) {
        if (ids.has(item.itemId)) {
            context.addIssue({
                code: "custom",
                message: "itemId must be unique",
                path: ["items", index],
            });
        }
        ids.add(item.itemId);
    }
});
export const operationIssueCodeSchema = z.enum([
    "purchase_rule_missing",
    "purchase_rule_ambiguous",
    "sale_rule_missing",
    "sale_rule_ambiguous",
    "purchase_value_missing",
    "sale_value_missing",
    "purchase_st_status_pending",
    "purchase_ipi_pending",
    "purchase_fcp_pending",
]);
const icmsModeSchema = z.enum(["ST", "STANDARD", "NOT_TAXED"]).nullable();
const routeSchema = z.object({ originUf: ufSchema, destinationUf: ufSchema }).strict();
export const operationPurchaseLegSchema = z
    .object({
    route: routeSchema,
    fiscalRuleId: z.string().uuid().nullable(),
    ipiRuleId: z.string().uuid().nullable(),
    icmsMode: icmsModeSchema,
    base: moneySchema,
    ipiRate: rateSchema,
    ipi: moneySchema,
    ownIcmsRate: rateSchema,
    ownIcms: moneySchema,
    originalMva: rateSchema,
    adjustedMva: rateSchema,
    stBase: moneySchema,
    /** ICMS-ST due on entry (paid by BackChannel or retained by the supplier). */
    stDue: moneySchema,
    /** CALCULATED from the rule; INVOICE: retained by the supplier as `purchaseStRetained` states. */
    stSource: z.enum(["CALCULATED", "INVOICE"]),
    /** Own ICMS BackChannel can credit; zero when ST closed the chain on entry. */
    icmsCredit: moneySchema,
    acquisitionCost: moneySchema,
    supplierType: operationSupplierTypeSchema.optional(),
    stPreviouslyPaid: z.boolean().optional(),
    /** Engine 1.7.0: the purchase used the regra geral (sem ST). */
    generalRule: saleGeneralRuleResultSchema.optional(),
    /** Engine 1.9.0: the purchase used the destination's published ST rule for another origin. */
    destinationStRule: destinationStRuleResultSchema.optional(),
    /**
     * Engine 1.10.0: FECP inside ALQ_intra of the entry ST. Present only when the ST is CALCULATED
     * and the destination's law puts FECP inside ALQ_intra.
     */
    fcpInAlqIntra: fcpInAlqIntraResultSchema.optional(),
    /** Engine 1.10.0: the sender withholds the entry ST under the named agreement. */
    stCollection: stCollectionResultSchema.optional(),
    /** Engine 1.10.0: the purchase own ICMS used the statutory interstate rate. */
    ownIcmsRateSource: purchaseOwnIcmsRateSchema.optional(),
    statutoryRate: statutoryOwnIcmsRateResultSchema.optional(),
})
    .strict();
export const operationSaleLegSchema = z
    .object({
    route: routeSchema,
    fiscalRuleId: z.string().uuid().nullable(),
    icmsMode: icmsModeSchema,
    base: moneySchema,
    /** True when entry ST already closed ICMS for an internal sale in the same UF. */
    stClosedOnEntry: z.boolean(),
    /**
     * Indicative CST of the sale: 60 ST charged earlier (entry ST closed an internal sale), 10 ST
     * on this invoice, 20 own ICMS at a reduced burden the rule states, 00 own ICMS, 40 exempt.
     */
    cst: z.enum(["00", "10", "20", "40", "60"]).nullable(),
    ownIcmsRate: rateSchema,
    ownIcms: moneySchema,
    adjustedMva: rateSchema,
    stBase: moneySchema,
    /** ICMS-ST BackChannel retains as substitute and charges on the invoice. */
    stCharged: moneySchema,
    /** False when the sale rule states no FCP/FECP rate: FCP is not tracked, not zero. */
    fcpTracked: z.boolean(),
    fcpRate: rateSchema.nullable(),
    /** FCP-ST charged on the invoice with the ICMS-ST: ST base × FCP. */
    fcpSt: moneySchema,
    difalRate: rateSchema,
    /** DIFAL due to the customer's UF for a final consumer (EC 87/2015). */
    difal: moneySchema,
    /** Destination FCP on the DIFAL of a final-consumer sale: sale value × FCP. */
    fcpDifal: moneySchema,
    /** Engine 1.9.0: the sale used the regra geral (sem ST). */
    generalRule: saleGeneralRuleResultSchema.optional(),
    /**
     * Engine 1.10.0: ALQ_intra of the sale ST with the destination's FECP inside, when that
     * adjustment was used.
     */
    alqIntra: rateSchema.optional(),
    fcpInAlqIntraBasis: fcpInAlqIntraResultSchema.shape.legalBasis.optional(),
})
    .strict();
export const operationTotalsSchema = z
    .object({
    /** max(0, sale own ICMS − entry credit). */
    icmsPayable: moneySchema,
    creditCarryover: moneySchema,
    /** Non-recoverable taxes carried in the acquisition cost (IPI + entry ST). */
    taxesInCost: moneySchema,
    /** Taxes BackChannel pays on the sale (ICMS payable + DIFAL + FCP on the DIFAL). */
    saleTaxesPaid: moneySchema,
    /** ICMS-ST and FCP-ST charged to the customer on the invoice (pass-through). */
    customerStCharged: moneySchema,
    invoiceTotal: moneySchema,
    /** IPI + entry ST + sale own ICMS − credit + DIFAL (negative when credit exceeds debit). PIS/COFINS excluded. */
    netTaxBurden: signedMoneySchema,
})
    .strict();
export const operationTraceStepSchema = z
    .object({
    leg: z.enum(["PURCHASE", "SALE", "TOTAL"]),
    code: z.string().min(1).max(80),
    formula: z.string().min(1).max(500),
    result: z.string().max(80),
    ruleId: z.string().uuid().nullable(),
})
    .strict();
export const operationItemResultSchema = z
    .object({
    itemId: z.string(),
    federalTaxes: operationFederalResultSchema.optional(),
    pricing: operationPricingResultSchema.optional(),
    unitValues: z
        .object({
        quantity: quantitySchema,
        purchaseValue: unitValueSchema,
        saleValue: unitValueSchema,
    })
        .strict()
        .optional(),
    status: z.enum(["CALCULATED", "REVIEW_REQUIRED"]),
    issues: z.array(operationIssueCodeSchema),
    purchase: operationPurchaseLegSchema,
    sale: operationSaleLegSchema,
    totals: operationTotalsSchema,
    evidence: z
        .array(z
        .object({
        ruleId: z.string().uuid(),
        leg: z.enum(["PURCHASE", "SALE"]),
        legalReference: z.string().nullable(),
        sourceUrl: z.string().nullable(),
    })
        .strict())
        .max(20),
    trace: z.array(operationTraceStepSchema).max(60),
})
    .strict();
export const operationSimulationResponseSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    calculationDate: z.string().date(),
    ruleset: z
        .object({
        id: z.string().uuid(),
        version: z.string().min(1),
        contentDigest: z.string().min(1),
        policyVersion: z.string().min(1),
    })
        .strict(),
    pisCofins: z.enum(["OUTSIDE_THIS_BASE", "GOVERNED_V1"]),
    items: z.array(operationItemResultSchema).min(1).max(50),
    stReview: operationStReviewSchema.optional(),
    /** Present only with X-Tax-Operation-Research: 1.0.0. */
    research: operationResearchStateSchema.optional(),
})
    .strict()
    .superRefine((response, context) => {
    if (response.stReview === undefined)
        return;
    if (response.stReview.rulesetContentDigest !== response.ruleset.contentDigest)
        context.addIssue({
            code: "custom",
            path: ["stReview", "rulesetContentDigest"],
            message: "ST_REVIEW_RULESET_DIGEST_MISMATCH",
        });
    const itemIds = new Set(response.items.map((item) => item.itemId));
    const reviewIds = new Set(response.stReview.items.map((item) => item.itemId));
    if (itemIds.size !== response.items.length ||
        itemIds.size !== reviewIds.size ||
        [...itemIds].some((itemId) => !reviewIds.has(itemId)))
        context.addIssue({
            code: "custom",
            path: ["stReview", "items"],
            message: "ST_REVIEW_ITEM_IDS_MISMATCH",
        });
});
//# sourceMappingURL=operation.js.map